import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Star, CheckCircle, ChevronLeft, User as UserIcon, Loader } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeIn} 0.3s ease;
`;

const Card = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  padding: 1.5rem;
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.25rem;
`;

const SubText = styled.p`
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0 0 1rem;
`;

const CoworkerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
`;

const CoworkerCard = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  background: ${p => p.$rated ? '#f0fdf4' : p.$selected ? '#eef2ff' : '#f8fafc'};
  border: 2px solid ${p => p.$rated ? '#86efac' : p.$selected ? '#818cf8' : '#e2e8f0'};
  border-radius: 10px;
  cursor: ${p => p.$rated ? 'default' : 'pointer'};
  text-align: left;
  transition: all 0.15s;
  opacity: ${p => p.$rated ? 0.7 : 1};

  &:hover {
    ${p => !p.$rated && `border-color: #818cf8; background: #eef2ff;`}
  }
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${p => p.$color || 'linear-gradient(135deg,#667eea,#764ba2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  flex-shrink: 0;
  overflow: hidden;

  img { width: 100%; height: 100%; object-fit: cover; }
`;

const CoworkerName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
`;

const CoworkerRole = styled.div`
  font-size: 0.6875rem;
  color: #94a3b8;
  text-transform: capitalize;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6366f1;
  background: transparent;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: all 0.15s;
  &:hover { background: #eef2ff; }
`;

const CategoryBlock = styled.div`
  padding: 1rem 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-of-type { border-bottom: none; }
`;

const CategoryName = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 0.5rem;
`;

const NoteRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
`;

const NoteLabel = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
  min-width: 120px;
  flex: 1;
  line-height: 1.4;
`;

const ScoreGroup = styled.div`
  display: flex;
  gap: 0.3rem;
`;

const ScoreBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1.5px solid ${p => p.$active ? '#f59e0b' : '#e2e8f0'};
  background: ${p => p.$active ? '#fef3c7' : '#fff'};
  color: ${p => p.$active ? '#92400e' : '#94a3b8'};
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.1s;

  &:hover {
    border-color: #f59e0b;
    background: #fffbeb;
    color: #92400e;
  }
`;

const CoefficientBadge = styled.span`
  font-size: 0.6875rem;
  color: #92400e;
  background: #fef3c7;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  font-weight: 600;
  margin-left: 0.25rem;
`;

const SubmitBtn = styled.button`
  padding: 0.7rem 2rem;
  border: none;
  border-radius: 10px;
  background: ${p => p.disabled ? '#94a3b8' : '#6366f1'};
  color: white;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.15s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;

  &:hover:not(:disabled) { background: #4f46e5; }
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  color: #166534;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #94a3b8;
  font-size: 0.9375rem;
`;

const SCORE_OPTIONS = [0, 1, 2, 3, 4, 5];

const AVATAR_COLORS = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#f093fb,#f5576c)',
  'linear-gradient(135deg,#4facfe,#00f2fe)',
  'linear-gradient(135deg,#43e97b,#38f9d7)',
  'linear-gradient(135deg,#fa709a,#fee140)',
  'linear-gradient(135deg,#a18cd1,#fbc2eb)',
];

const PeerRatingPage = () => {
  const { t } = useTranslation();
  const [coworkers, setCoworkers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [coefficients, setCoefficients] = useState([]);
  const [ratedIds, setRatedIds] = useState([]);
  const [selectedCoworker, setSelectedCoworker] = useState(null);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [cwRes, cfgRes, stRes] = await Promise.all([
        axios.get('/api/peer-ratings/coworkers', { headers }),
        axios.get('/api/peer-ratings/config', { headers }),
        axios.get('/api/peer-ratings/status', { headers }),
      ]);
      setCoworkers(cwRes.data.coworkers || []);
      setCategories(cfgRes.data.categories || []);
      setCoefficients(cfgRes.data.coefficients || []);
      setRatedIds(stRes.data.ratedIds || []);
    } catch (err) {
      console.error('Failed to load peer rating data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const initScores = useCallback((cats) => {
    const s = {};
    cats.forEach((cat, ci) => {
      const notes = (cat.noteSections || []).filter(n => n && n.trim());
      s[ci] = {};
      notes.forEach((_, ni) => { s[ci][ni] = null; });
    });
    setScores(s);
  }, []);

  const selectCoworker = (cw) => {
    if (ratedIds.includes(cw._id)) return;
    setSelectedCoworker(cw);
    setSubmitSuccess('');
    initScores(categories);
  };

  const setScore = (catIdx, noteIdx, value) => {
    setScores(prev => ({
      ...prev,
      [catIdx]: { ...prev[catIdx], [noteIdx]: value }
    }));
  };

  const coefficientForNote = (cat, noteIdx) => {
    if (Array.isArray(cat?.coefficients) && cat.coefficients[noteIdx] != null) {
      return cat.coefficients[noteIdx];
    }
    return coefficients[noteIdx];
  };

  const allScored = () => {
    return categories.every((cat, ci) => {
      const notes = (cat.noteSections || []).filter(n => n && n.trim());
      return notes.every((_, ni) => scores[ci]?.[ni] != null);
    });
  };

  const handleSubmit = async () => {
    if (!selectedCoworker || !allScored()) return;
    setSubmitting(true);
    try {
      const ratings = categories.map((cat, ci) => {
        const notes = (cat.noteSections || []).filter(n => n && n.trim());
        return {
          categoryName: cat.name,
          noteScores: notes.map((_, ni) => scores[ci]?.[ni] ?? 0)
        };
      });
      await axios.post('/api/peer-ratings/submit', {
        rateeId: selectedCoworker._id,
        ratings
      }, { headers });

      setRatedIds(prev => [...prev, selectedCoworker._id]);
      setSubmitSuccess(`Rating for ${selectedCoworker.firstName} submitted!`);
      setSelectedCoworker(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (cw) =>
    `${cw.firstName?.charAt(0) || ''}${cw.lastName?.charAt(0) || ''}`.toUpperCase() || '?';

  if (loading) {
    return (
      <Container>
        <EmptyState><Loader size={24} style={{ animation: 'spin 1s linear infinite' }} /> {t('peerRating.loading')}</EmptyState>
      </Container>
    );
  }

  if (coworkers.length === 0) {
    return (
      <Container>
        <Card>
          <EmptyState>
            <UserIcon size={36} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
            <div>{t('peerRating.noCoworkersFoundInYour')}</div>
          </EmptyState>
        </Card>
      </Container>
    );
  }

  if (categories.length === 0) {
    return (
      <Container>
        <Card>
          <EmptyState>
            <Star size={36} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
            <div>{t('peerRating.peerRatingsAreNotConfigured')}</div>
          </EmptyState>
        </Card>
      </Container>
    );
  }

  const allRated = coworkers.length > 0 && coworkers.every(cw => ratedIds.includes(cw._id));

  return (
    <Container>
      {submitSuccess && (
        <SuccessBanner><CheckCircle size={18} /> {submitSuccess}</SuccessBanner>
      )}

      {allRated && (
        <SuccessBanner>
          <CheckCircle size={18} /> {t('peerRating.youHaveRatedAllYour')}
        </SuccessBanner>
      )}

      {!selectedCoworker ? (
        <Card>
          <SectionTitle>{t('peerRating.rateYourPeers')}</SectionTitle>
          <SubText>
            {t('peerRating.selectACoworkerToRate')}
          </SubText>
          <CoworkerGrid>
            {coworkers.map((cw, i) => {
              const rated = ratedIds.includes(cw._id);
              return (
                <CoworkerCard
                  key={cw._id}
                  $rated={rated}
                  onClick={() => selectCoworker(cw)}
                >
                  <Avatar $color={AVATAR_COLORS[i % AVATAR_COLORS.length]}>
                    {cw.profileImage
                      ? <img src={cw.profileImage} alt={cw.firstName} />
                      : getInitials(cw)}
                  </Avatar>
                  <div>
                    <CoworkerName>
                      {cw.firstName} {cw.lastName}
                      {rated && <CheckCircle size={14} style={{ marginLeft: 4, color: '#22c55e', verticalAlign: 'middle' }} />}
                    </CoworkerName>
                    <CoworkerRole>{(cw.role || '').replace(/_/g, ' ')}</CoworkerRole>
                  </div>
                </CoworkerCard>
              );
            })}
          </CoworkerGrid>
        </Card>
      ) : (
        <>
          <BackButton onClick={() => { setSelectedCoworker(null); setSubmitSuccess(''); }}>
            <ChevronLeft size={16} /> {t('peerRating.backToList')}
          </BackButton>

          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
              <Avatar $color={AVATAR_COLORS[coworkers.indexOf(selectedCoworker) % AVATAR_COLORS.length]} style={{ width: 44, height: 44, fontSize: '1rem' }}>
                {selectedCoworker.profileImage
                  ? <img src={selectedCoworker.profileImage} alt="" />
                  : getInitials(selectedCoworker)}
              </Avatar>
              <div>
                <SectionTitle style={{ marginBottom: 0 }}>
                  {t('peerRating.rating')} {selectedCoworker.firstName} {selectedCoworker.lastName}
                </SectionTitle>
                <SubText style={{ margin: 0 }}>{t('peerRating.scoreEachItemFrom0')}</SubText>
              </div>
            </div>

            {categories.map((cat, ci) => {
              const notes = (cat.noteSections || []).filter(n => n && n.trim());
              if (notes.length === 0) return null;
              return (
                <CategoryBlock key={ci}>
                  <CategoryName>
                    <Star size={14} style={{ marginRight: 4, color: '#f59e0b', verticalAlign: 'middle' }} />
                    {cat.name}
                  </CategoryName>
                  {notes.map((noteText, ni) => (
                    <NoteRow key={ni}>
                      <NoteLabel>
                        {noteText}
                        {coefficientForNote(cat, ni) != null && coefficientForNote(cat, ni) !== 1 && (
                          <CoefficientBadge>x{coefficientForNote(cat, ni)}</CoefficientBadge>
                        )}
                      </NoteLabel>
                      <ScoreGroup>
                        {SCORE_OPTIONS.map(v => (
                          <ScoreBtn
                            key={v}
                            $active={scores[ci]?.[ni] === v}
                            onClick={() => setScore(ci, ni, v)}
                          >
                            {v}
                          </ScoreBtn>
                        ))}
                      </ScoreGroup>
                    </NoteRow>
                  ))}
                </CategoryBlock>
              );
            })}

            <SubmitBtn disabled={submitting || !allScored()} onClick={handleSubmit}>
              {submitting ? <><Loader size={16} /> {t('peerRating.submitting')}</> : t('peerRating.submitRating')}
            </SubmitBtn>
          </Card>
        </>
      )}
    </Container>
  );
};

export default PeerRatingPage;
